import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Upload, Button, notification, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import DebouncedSelect from "../../Component/DebouncedSelect";
import CustomSelectField from "../../Component/CustomSelectField";
import { fetchItineraries } from "../../Services/user_question_history";
import { getAllRecommendations } from "../../Services/recommendation";

const EditModal = ({
  isModalVisible,
  handleOk,
  handleCancel,
  loading,
  initialValues,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedState, setSelectedState] = useState(null);

  const { data: recommendationsData, isLoading: recommendationsLoading } = useQuery({
    queryKey: ["recommendations_all_for_trending_edit", isModalVisible],
    queryFn: () => getAllRecommendations({ skip: 0, limit: 1000 }),
    enabled: isModalVisible,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const recommendationOptions = (recommendationsData?.recommendations || []).map((r) => ({
    label: r.name,
    value: r.id,
  }));

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        destination_text: initialValues.destination_text,
        destination_address: initialValues.destination_address,
        itinerary_id: initialValues.itinerary_id,
        recommendation_ids: initialValues.recommendation_ids || [],
      });

      if (initialValues.full_image_url) {
        const fileData = {
          uid: "-1",
          name: "image",
          status: "done",
          url: initialValues.full_image_url,
        };
        setFileList([fileData]);
        form.setFieldsValue({ image: fileData });
      } else {
        setFileList([]);
      }
      setSelectedState(null);

      // Prefill itinerary field
      if (initialValues.itinerary_id) {
        fetchItineraries(0, "", initialValues.itinerary_id).then((options) => {
          const found = options.find((opt) => opt.value === initialValues.itinerary_id);
          
          if (found) {
              form.setFieldsValue({ itinerary_id: found?.value });     
              setSelectedState({ label: "#"+found?.value +" - " + found?.label, id: found?.value });
          } else {
            console.warn("Itinerary not found in fetched options");
          }
        });
      }
    }
  }, [initialValues, form, isModalVisible]);

  const handleChange = ({ fileList }) => {
    setFileList(fileList.slice(-1));
    form.setFieldsValue({ image: fileList.length > 0 ? fileList[0] : null });
  };

  const handlePreview = async (file) => {
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Select</div>
    </div>
  );

  const onFinish = async (values) => {
    const updatedValues = {};

    Object.keys(values).forEach((key) => {
      if (
        key === "image" ||
        key === "itinerary_id" ||
        key === "recommendation_ids"
      ) return;
      if (values[key] !== initialValues[key]) {
        updatedValues[key] = values[key];
      }
    });

    // Compare recommendation_ids as arrays
    const newRecIds = values.recommendation_ids || [];
    const oldRecIds = initialValues?.recommendation_ids || [];
    const sameRecIds =
      newRecIds.length === oldRecIds.length &&
      [...newRecIds].sort().every((v, i) => v === [...oldRecIds].sort()[i]);
    if (!sameRecIds) {
      updatedValues.recommendation_ids = newRecIds;
    }

    if (fileList.length > 0 && fileList[0]?.originFileObj) {
      updatedValues.image = fileList[0].originFileObj;
    }
    console.log("onfinish=", values, initialValues.itinerary_id)
    if (values.itinerary_id !== initialValues.itinerary_id) {
      updatedValues.itinerary_id = values.itinerary_id; // primitive
    }
    if (values.itinerary_id === undefined && initialValues.itinerary_id) {
      updatedValues.itinerary_id = null;
      updatedValues.is_itinerary_remove = true;
    }


    if (Object.keys(updatedValues).length > 0) {
      await handleOk(initialValues?.id, updatedValues);
    } else {
      notification.error({
        message: "No changes detected",
        duration: 2,
      });
    }
  };

  return (
    <Modal
      title="Edit Trending Destination"
      open={isModalVisible}
      footer={null}
      onCancel={() => {
        handleCancel();
        form.resetFields();
        setFileList([]);
        setPreviewImage("");
      }}
      maskClosable={false}
      style={{ top: 10 }}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="destination_text"
          label="Destination Place"
          rules={[{ required: true, message: "Please input the Destination Place!" }]}
        >
          <Input placeholder="Enter Destination Place" />
        </Form.Item>

        <Form.Item
          name="destination_address"
          label="Destination Location"
          rules={[{ required: true, message: "Please select a Destination Location" }]}
        >
          <DebouncedSelect
            placeholder="Type at least 4 characters to search address"
            value={form.getFieldValue("destination_address")}
            onChange={(value) => form.setFieldsValue({ destination_address: value })}
          />
        </Form.Item>

        <Form.Item
          label="Itinerary"
          name="itinerary_id"
        >
          <CustomSelectField
            label="Itinerary"
            fetchOptions={fetchItineraries}
            initialValue={selectedState}
            onChange={(value) => {
              console.log("onchange itinerary_id", value)
              form.setFieldsValue({ itinerary_id: value });
            }}
            formatLabel={(option) => (
              <>
                <div>{`#${option.value} - ${option.label}`}</div>
                {/* <div style={{ fontSize: "12px", color: "#888" }}>{option.category_name}</div> */}
              </>
            )}
          />

        </Form.Item>

        <Form.Item
          name="recommendation_ids"
          label="Recommendations"
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Select recommendations"
            loading={recommendationsLoading}
            options={recommendationOptions}
            optionFilterProp="label"
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item
          name="image"
          label="Select Image"
          rules={[{ required: true, message: "Please upload an image" }]}
        >
          <Upload
            listType="picture-card"
            fileList={fileList}
            onPreview={handlePreview}
            onChange={handleChange}
            beforeUpload={() => false}
            maxCount={1}
            accept=".jpg,.jpeg,.png,.svg"
            onRemove={() =>
              new Promise((resolve) => {
                Modal.confirm({
                  title: "Are you sure you want to delete this image?",
                  onOk: () => {
                    setFileList([]);
                    form.setFieldsValue({ image: null });
                    resolve();
                  },
                });
              })
            }
          >
            {fileList.length >= 1 ? null : uploadButton}
          </Upload>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ width: "100%" }}
          >
            Save Changes
          </Button>
        </Form.Item>
      </Form>

      <Modal
        open={previewOpen}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
      >
        <img alt="Preview" style={{ width: "100%" }} src={previewImage} />
      </Modal>
    </Modal>
  );
};

export default EditModal;
